'use client'

import { useEffect, useState, useTransition, useRef } from 'react'
import { Loader2, Loader2Icon, Trash } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useProjectStore } from '@/hooks/use-project-store'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import { popModal, pushModal } from '@/components/modals'
import { PremiumIcon } from '@/components/icons/premium'
import { CrownIcon } from '@/components/icons'
import { cn, generateShortId } from '@/lib/utils'
import { useProjectPublish } from '@/hooks/use-project-publish'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import entri, { type EntriConfig } from 'entrijs'
import type { Project } from '@/lib/types'
import { useSubscriptionCheck } from '@/hooks/subscriptions/use-subscription-check'
import { APPLICATION_URL, WORKER_APPLICATION_URL } from '@/lib/constant'
import { getFileSystemProjectConfig } from '@/lib/file-system-tree'
import { useProductApi } from '../project/toolbox-overlay/shopping-admin/api/product-api'
import { toast } from 'sonner'
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react'
import Image from 'next/image'
import { GradientTooltip } from '@/components/common/gradient-tooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

interface UpgradeButtonProps {
  onClick: () => void
  isLoading: boolean
  user: any
  project: any
  normalText: string
  className?: string
  size?: 'sm' | 'lg' | 'default'
}

function UpgradeButton({
  onClick,
  isLoading,
  className = '',
  size = 'sm',
}: Omit<UpgradeButtonProps, 'user' | 'project' | 'normalText'>) {
  const t = useTranslations('publish-modal')

  return (
    <GradientTooltip content={t('connect_premium_tooltip')}>
      <Button
        onClick={onClick}
        className={`bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg px-4 py-2 h-auto flex items-center gap-2 ${className}`}
        disabled={isLoading}
        size={size}
      >
        {isLoading ? <Loader2 className="animate-spin w-4 h-4" /> : null}
        {t('connect_button')}
        <PremiumIcon width={16} height={16} />
      </Button>
    </GradientTooltip>
  )
}

const getConnectDomainEntriConfig = (project: Project): EntriConfig => {
  const applicationUrl =
    project.template_type === 'worker'
      ? WORKER_APPLICATION_URL
      : APPLICATION_URL

  return {
    applicationId: process.env.NEXT_PUBLIC_ENTRI_APP_ID!,
    hostRequired: false,
    dnsRecords: {
      domain: [
        {
          type: 'A',
          host: '@',
          value: '{ENTRI_SERVERS}',
          ttl: 300,
          ssl: true,
          applicationUrl,
        },
      ],
      subDomain: [
        {
          type: 'CNAME',
          host: '{SUBDOMAIN}',
          value: '{CNAME_TARGET}',
          ttl: 300,
          ssl: true,
          applicationUrl,
        },
      ],
    },
  }
}

const getBuyDomainEntriConfig = (project: Project): EntriConfig => {
  const applicationUrl =
    project.template_type === 'worker'
      ? WORKER_APPLICATION_URL
      : APPLICATION_URL

  return {
    applicationId: process.env.NEXT_PUBLIC_ENTRI_APP_ID!,
    hostRequired: false,
    dnsRecords: [
      {
        type: 'A',
        host: '@',
        value: '{ENTRI_SERVERS}',
        ttl: 300,
        ssl: true,
        applicationUrl,
      },
    ],
  }
}

export function PublishModal() {
  const t = useTranslations('publish-modal')
  const tEmbed = useTranslations('embed-modal')
  const { project, updateProject, getFileSystemTree, getProject } =
    useProjectStore()
  const { data: user } = useCurrentUser()
  const { _id = '' } = project || {}
  const projectId = _id.toString()
  const { publish } = useProjectPublish()
  const [isPublishing, setIsPublishing] = useState(false)
  const [isEmbedding, setIsEmbedding] = useState(false)
  const [showEmbedFlow, setShowEmbedFlow] = useState(false)
  const [embedProjectUrl, setEmbedProjectUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [copiedDomain, setCopiedDomain] = useState(false)
  const [stepsExpanded, setStepsExpanded] = useState(false)
  const [connectDomainLoading, startConnectDomain] = useTransition()
  const [buyDomainLoading, startBuyDomain] = useTransition()
  const [entryType, setEntryType] = useState<string>()
  const [domainStatus, setDomainStatus] = useState<Record<string, boolean>>()
  const { features, loading: subscriptionLoading } =
    useSubscriptionCheck(project)
  const { getProducts } = useProductApi()
  const {
    workspaces,
    defaultWorkspace,
    loading: workspacesLoading,
  } = useWorkspaces()

  // Combined loading state for subscription-related data
  const isSubscriptionDataLoading = subscriptionLoading || workspacesLoading

  // Disconnect domain confirmation dialog state
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [disconnectDomainType, setDisconnectDomainType] = useState<
    'own' | 'new' | null
  >(null)
  const [disconnectReasons, setDisconnectReasons] = useState<string[]>([])
  const [disconnectOtherReason, setDisconnectOtherReason] = useState('')

  const disconnectReasonOptions = [
    { id: 'moving', label: t('disconnect_reason_moving') },
    { id: 'not_needed', label: t('disconnect_reason_not_needed') },
    { id: 'domain_issues', label: t('disconnect_reason_domain_issues') },
    { id: 'cost', label: t('disconnect_reason_cost') },
    { id: 'other', label: t('disconnect_reason_other') },
  ]

  // Find the workspace for this specific project
  // If project has workspace_id, use that specific workspace
  // Otherwise, use the user's default workspace (for personal projects)
  const projectWorkspace = project?.workspace_id
    ? workspaces.find((w) => w._id.toString() === project.workspace_id)
    : defaultWorkspace

  // console.log('projectWorkspace', projectWorkspace)
  // Compute domain limits from the workspace data
  const domainLimits = projectWorkspace
    ? {
        max_domains: projectWorkspace.limits.max_domains,
        current_domains: projectWorkspace.limits.current_domains,
        remaining_domains: projectWorkspace.limits.remaining_domains,
        can_create_more: projectWorkspace.limits.can_create_more,
      }
    : null

  // Track previous domain values to detect when they're added
  const prevOwnDomainRef = useRef(project?.own_domain)
  const prevNewDomainRef = useRef(project?.new_domain)
  const lastDomainBindingRef = useRef<{
    domain?: string
    domainType?: 'own' | 'new'
    entryType?: 'connect' | 'buy'
  }>({})

  // Generate embed code with proper formatting
  const embedCode = embedProjectUrl
    ? `<div style="padding: 10px 0; border-radius: 12px; overflow: hidden;">
  <iframe
    src="${embedProjectUrl}"
    style="width: 100%; aspect-ratio: 16 / 9; border: none; border-radius: 12px;"
    allowfullscreen>
  </iframe>
</div>`
    : ''

  const handleCopyEmbed = async () => {
    if (!embedProjectUrl || !embedCode) {
      toast.error(
        'Project URL is not available. Please publish the project first.',
      )
      return
    }

    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      toast.success('Embed code copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy embed code:', error)
      toast.error('Failed to copy embed code. Please try again.')
    }
  }

  // Watch for new domains being added and auto-publish
  useEffect(() => {
    const ownDomainAdded = !prevOwnDomainRef.current && project?.own_domain
    const newDomainAdded = !prevNewDomainRef.current && project?.new_domain
    const domainType = ownDomainAdded ? 'own' : newDomainAdded ? 'new' : null
    const domainValue =
      domainType === 'own' ? project?.own_domain : project?.new_domain

    if (domainType && domainValue) {
      console.log('New domain detected, auto-publishing with animation...')

      // Simulate button press animation
      setIsPublishing(true)

      // Trigger publish after brief delay for animation
      setTimeout(() => {
        publish({
          successToast: true,
          needPlayConfetti: true,
          needShowPublishResult: true,
        })
          .then(() => {
            console.log('Auto-publish completed successfully')
            popModal('PublishModal')
            const entryType =
              lastDomainBindingRef.current?.entryType ||
              (domainType === 'own' ? 'connect' : 'buy')
            sendToBothAnalytics(
              MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_DOMAIN_PUBLISH_SUCCESS,
              {
                project_id: projectId,
                domain: domainValue,
                domain_type: domainType,
                entry_type: entryType,
                workspace_id: project?.workspace_id?.toString(),
              },
              user?._id?.toString(),
            )
            lastDomainBindingRef.current = {}
          })
          .catch((error) => {
            console.error('Auto-publish failed:', error)
          })
          .finally(() => {
            setIsPublishing(false)
          })
      }, 300)
    }

    // Update refs for next comparison
    prevOwnDomainRef.current = project?.own_domain
    prevNewDomainRef.current = project?.new_domain
  }, [
    project?.own_domain,
    project?.new_domain,
    project?.workspace_id,
    projectId,
    publish,
    user?._id,
  ])

  useEffect(() => {
    const domains = [project?.own_domain, project?.new_domain].filter(Boolean)
    if (domains.length === 0) {
      setDomainStatus({})
      return
    }
    const loadDomainStatus = async () => {
      try {
        const result = await fetch('/api/entri/status', {
          method: 'POST',
          body: JSON.stringify({ domains }),
        })
        if (result.ok) {
          const data = await result.json()
          setDomainStatus(data)
        } else {
          setDomainStatus({})
        }
      } catch (error) {
        console.error('Error loading domain status:', error)
        setDomainStatus({})
      }
    }
    loadDomainStatus()
  }, [project?.own_domain, project?.new_domain])

  useEffect(() => {
    if (!project?._id) return

    const onEntriClose = (event: any) => {
      if (event?.detail?.success) {
        const { domain } = event.detail
        if (!domain) return

        const resolvedEntryType: 'connect' | 'buy' =
          entryType === 'buy' ? 'buy' : 'connect'
        const domainType = resolvedEntryType === 'connect' ? 'own' : 'new'

        if (resolvedEntryType === 'connect') {
          updateProject({ own_domain: domain })
        } else {
          updateProject({ new_domain: domain })
        }
        fetch('/api/entri/bind', {
          method: 'POST',
          body: JSON.stringify({
            type: resolvedEntryType,
            projectId: project?._id,
            customDomain: domain,
          }),
        })

        sendToBothAnalytics(
          MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_DOMAIN_BINDING_SUCCESS,
          {
            project_id: projectId,
            domain,
            domain_type: domainType,
            entry_type: resolvedEntryType,
            workspace_id: project?.workspace_id?.toString(),
          },
          user?._id?.toString(),
        )

        lastDomainBindingRef.current = {
          domain,
          domainType,
          entryType: resolvedEntryType,
        }
        // Note: Auto-publish is handled by the domain detection useEffect
      }
    }
    window.addEventListener('onEntriClose', onEntriClose)

    return () => {
      window.removeEventListener('onEntriClose', onEntriClose)
    }
  }, [project?._id, project?.workspace_id, entryType, projectId, user?._id])

  const onConnectDomain = async () => {
    if (!features.connectDomain) {
      // Free users (max_domains = 0) should just upgrade, no need to mention additional domains
      const isFreeUser = domainLimits && domainLimits.max_domains === 0

      if (isFreeUser) {
        // Free user: just ask them to upgrade
        pushModal('PricingModal', {
          type: 'need_basic',
          trigger: 'custom-domain',
          minRequiredPlan: 'basic',
        })
      } else {
        // Subscribed user who reached limit: recommend adding additional domains
        pushModal('PricingModal', {
          type: 'need_basic',
          trigger: 'custom-domain',
          minRequiredPlan: 'basic',
          domainLimits: domainLimits,
        })
      }
      return
    }
    startConnectDomain(async () => {
      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_DOMAIN_BINDING_START,
        {
          project_id: projectId,
          entry_type: 'connect',
          workspace_id: project?.workspace_id?.toString(),
          domain_limits: domainLimits,
        },
        user?._id?.toString(),
      )
      setEntryType('connect')
      try {
        const response = await fetch('/api/entri/token')

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.error) {
          throw new Error(data.error)
        }

        const { token } = data

        const config = {
          ...getConnectDomainEntriConfig(project!),
          token,
        }
        console.log(
          '[Entri] Calling entri.showEntri with config:',
          JSON.stringify(config, null, 2),
        )

        const result = await entri.showEntri(config)
        console.log('[Entri] showEntri returned:', result)
      } catch (error) {
        console.error('Error getting Entri token for connect domain:', error)
        toast.error('Failed to connect domain. Please try again.')
      }
    })
  }

  const onBuyDomain = async () => {
    if (!features.buyDomain) {
      // Free users (max_domains = 0) should just upgrade, no need to mention additional domains
      const isFreeUser = domainLimits && domainLimits.max_domains === 0

      if (isFreeUser) {
        // Free user: just ask them to upgrade
        pushModal('PricingModal', {
          type: 'need_basic',
          trigger: 'buy-domain',
          minRequiredPlan: 'basic',
        })
      } else {
        // Subscribed user who reached limit: recommend adding additional domains
        pushModal('PricingModal', {
          type: 'need_basic',
          trigger: 'buy-domain',
          minRequiredPlan: 'basic',
          domainLimits: domainLimits,
        })
      }
      return
    }
    startBuyDomain(async () => {
      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_DOMAIN_BINDING_START,
        {
          project_id: projectId,
          entry_type: 'buy',
          workspace_id: project?.workspace_id?.toString(),
          domain_limits: domainLimits,
        },
        user?._id?.toString(),
      )
      setEntryType('buy')
      try {
        const response = await fetch('/api/entri/token')

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.error) {
          throw new Error(data.error)
        }

        const { token } = data

        const config = {
          ...getBuyDomainEntriConfig(project!),
          token,
        }
        console.log(
          '[Entri] Calling entri.purchaseDomain with config:',
          JSON.stringify(config, null, 2),
        )

        const result = await entri.purchaseDomain(config)
        console.log('[Entri] purchaseDomain returned:', result)
      } catch (error) {
        console.error('Error getting Entri token for buy domain:', error)
        toast.error('Failed to purchase domain. Please try again.')
      }
    })
  }

  const onUpgradeForConnectDomain = () => {
    // Free users (max_domains = 0) should just upgrade, no need to mention additional domains
    const isFreeUser = domainLimits && domainLimits.max_domains === 0

    if (isFreeUser) {
      // Free user: just ask them to upgrade
      pushModal('PricingModal', {
        type: 'need_basic',
        trigger: 'custom-domain',
        minRequiredPlan: 'basic',
      })
    } else {
      // Subscribed user who reached limit: recommend adding additional domains
      pushModal('PricingModal', {
        type: 'need_basic',
        trigger: 'custom-domain',
        minRequiredPlan: 'basic',
        domainLimits: domainLimits,
      })
    }
  }

  const onUpgradeForBuyDomain = () => {
    // Free users (max_domains = 0) should just upgrade, no need to mention additional domains
    const isFreeUser = domainLimits && domainLimits.max_domains === 0

    if (isFreeUser) {
      // Free user: just ask them to upgrade
      pushModal('PricingModal', {
        type: 'need_basic',
        trigger: 'buy-domain',
        minRequiredPlan: 'basic',
      })
    } else {
      // Subscribed user who reached limit: recommend adding additional domains
      pushModal('PricingModal', {
        type: 'need_basic',
        trigger: 'buy-domain',
        minRequiredPlan: 'basic',
        domainLimits: domainLimits,
      })
    }
  }

  const onUnbindDomain = (type: 'own' | 'new') => {
    // Show confirmation dialog instead of directly unbinding
    setDisconnectDomainType(type)
    setDisconnectReasons([])
    setDisconnectOtherReason('')
    setShowDisconnectDialog(true)
  }

  const onConfirmUnbindDomain = async () => {
    if (!disconnectDomainType) {
      return
    }

    setIsDisconnecting(true)

    try {
      const type = disconnectDomainType
      const updateData: Partial<Project> = {}
      if (type === 'own') {
        updateData.own_domain = undefined
      }
      if (type === 'new') {
        updateData.new_domain = undefined
      }
      const customDomain =
        type === 'own' ? project?.own_domain : project?.new_domain

      const response = await fetch('/api/entri/unbind', {
        method: 'POST',
        body: JSON.stringify({
          projectId: project?._id,
          customDomain,
          domainType: type,
          reasons: disconnectReasons,
          otherReason: disconnectOtherReason,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to disconnect domain')
      }

      // Update local state after successful API call
      updateProject({ ...updateData })

      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_DOMAIN_UNBINDING,
        {
          project_id: project?._id?.toString(),
          domain: customDomain,
          domain_type: type,
          workspace_id: project?.workspace_id?.toString(),
          disconnect_reasons: disconnectReasons,
          disconnect_other_reason: disconnectOtherReason,
        },
        user?._id?.toString(),
      )

      // Reset and close dialog
      setShowDisconnectDialog(false)
      setDisconnectDomainType(null)
      setDisconnectReasons([])
      setDisconnectOtherReason('')
    } catch (error) {
      console.error('Failed to disconnect domain:', error)
      toast.error('Failed to disconnect domain. Please try again.')
    } finally {
      setIsDisconnecting(false)
    }
  }

  const toggleDisconnectReason = (reasonId: string) => {
    setDisconnectReasons((prev) => {
      return prev.includes(reasonId)
        ? prev.filter((r) => r !== reasonId)
        : [...prev, reasonId]
    })
  }

  const getDomainStatus = (domain: string) => {
    // Loading state - when domainStatus is null (still fetching)
    if (!domainStatus) {
      return (
        <div className="text-sm font-semibold text-[#6B7280] bg-[#6B7280]/10 py-1.5 px-3 rounded-lg">
          {t('status.loading')}
        </div>
      )
    }

    const verified = Boolean(domainStatus[domain])
    if (verified) {
      return (
        <div className="text-sm font-semibold text-[#27E67A] bg-[#27E67A]/10 py-1.5 px-3 rounded-[50px]">
          {t('status.connected')}
        </div>
      )
    } else {
      return (
        <div className="text-sm font-semibold text-[#FFE838] bg-[#FFE838]/10 py-1.5 px-3 rounded-[50px]">
          {t('status.pending')}
        </div>
      )
    }
  }

  const subdomain =
    user?.username ||
    (user?._id ? generateShortId(user._id.toString()) : 'username')
  const projectUrl =
    project?.template_type === 'worker'
      ? `https://${project._id}.heyboss.live`
      : `https://${subdomain}.heyboss.tech/${generateShortId(projectId)}/`

  const onEmbedConfirmed = async () => {
    setIsEmbedding(true)

    try {
      // Publish the project first
      await publish({
        needShowPublishResult: false,
        successToast: false,
      })

      // Get the updated project URL after publishing
      const updatedProject = getProject()
      if (!updatedProject) {
        throw new Error('Project not found after publishing')
      }

      const projectUrl =
        updatedProject?.build_url ||
        (updatedProject?.template_type === 'worker'
          ? `https://${updatedProject._id}.heyboss.live`
          : `https://${subdomain}.heyboss.tech/${generateShortId(projectId)}/`)

      if (!projectUrl) {
        throw new Error('Failed to generate project URL')
      }

      // Store the project URL and transition to embed flow
      setEmbedProjectUrl(projectUrl)
      setShowEmbedFlow(true)
    } catch (error) {
      console.error('Failed to publish project for embed:', error)
      toast.error('Failed to publish project. Please try again.')
      setIsEmbedding(false)
    }
  }

  const onEmbed = () => {
    pushModal('EmbedConfirmationModal', {
      onConfirm: onEmbedConfirmed,
    })
  }

  const canPublishEcommerce = async () => {
    // Check ecommerce access (workspace OR project OR user premium)
    if (features.ecommerce) {
      return true
    }

    const projectConfig = getFileSystemProjectConfig(getFileSystemTree()!)
    const enableEcommerce = projectConfig.enable_e_commerce === true
    console.log('enableEcommerce', enableEcommerce)
    if (!enableEcommerce) {
      return true
    }

    const { data: products = [] } = await getProducts()
    console.log('products.length', products.length)
    if (products.length === 0) {
      return true
    }
    return false
  }

  const onPublish = async () => {
    setIsPublishing(true)
    const canPublish = await canPublishEcommerce().finally(() => {
      setIsPublishing(false)
    })
    if (!canPublish) {
      pushModal('PricingModal', {
        type: 'need_premium',
        trigger: 'ecommerce',
        projectId: project?._id.toString(),
        minRequiredPlan: 'premium',
      })
      return
    }

    setIsPublishing(true)
    // Check if project has products and verify premium access
    try {
      const response = await fetch(`/api/projects/${project?._id}/products`)
      if (response.ok) {
        const products = await response.json()
        if (products.length > 0) {
          // Project has products, check if user has premium access
          if (!features.payments) {
            pushModal('PricingModal', {
              type: 'need_premium_for_payments',
              trigger: 'payment',
              minRequiredPlan: 'premium',
            })
            setIsPublishing(false)
            return
          }
        }
      }
    } catch (error) {
      console.error('Error checking products:', error)
    }

    // Collect all connected domains
    const connectedDomains = []

    // Always include the default heyboss domain
    connectedDomains.push(projectUrl)

    // Add custom domains if they're connected
    if (project?.own_domain && domainStatus?.[project.own_domain]) {
      connectedDomains.push(`https://${project.own_domain}`)
    }

    if (project?.new_domain && domainStatus?.[project.new_domain]) {
      connectedDomains.push(`https://${project.new_domain}`)
    }

    console.log('Publishing to all connected domains:', connectedDomains)

    updateProject({
      deploy_url: projectUrl, // Keep the default as deploy_url
      build_url: projectUrl, // Use default for build_url (backend should handle all domains)
    })

    try {
      await publish({
        successToast: true,
        needPlayConfetti: true,
        needShowPublishResult: true,
      })
      popModal('PublishModal')
    } finally {
      setIsPublishing(false)
    }
  }

  // Render embed content when in embed flow (using original EmbedModal design)
  if (showEmbedFlow) {
    return (
      <DialogContent className="w-[80%] sm:w-[90%] max-w-[800px] max-h-[90vh] p-0 rounded-[20px] border-none">
        <div className="bg-[#1a1a1a] text-white flex flex-col max-h-[90vh] rounded-[20px]">
          <DialogHeader className="p-6 pb-4 flex-shrink-0">
            <DialogTitle className="text-xl font-semibold text-white">
              {tEmbed('embed_ready')}
            </DialogTitle>
            <p className="text-gray-300 mt-2">
              {tEmbed('embed_ready_description')}
            </p>
          </DialogHeader>

          <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
            {/* Instructions - Left Side */}
            <div className="w-full lg:w-2/5 p-6 pt-2 lg:pt-6 flex-shrink-0">
              {/* Mobile Collapsible Steps */}
              <div className="lg:hidden">
                <button
                  type="button"
                  onClick={() => setStepsExpanded(!stepsExpanded)}
                  className="flex items-center justify-between w-full text-left p-3 bg-white/[0.06] rounded-lg hover:bg-white/10 transition-colors"
                >
                  <span className="font-medium text-white">
                    {tEmbed('instructions')}
                  </span>
                  {stepsExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {stepsExpanded && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                        1
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-1">
                          {tEmbed('step1_title')}
                        </h3>
                        <p className="text-sm text-gray-300">
                          {tEmbed('step1_description')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                        2
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-1">
                          {tEmbed('step2_title')}
                        </h3>
                        <p className="text-sm text-gray-300">
                          {tEmbed('step2_description')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                        3
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-1">
                          {tEmbed('step3_title')}
                        </h3>
                        <p className="text-sm text-gray-300">
                          {tEmbed('step3_description')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Always Visible Steps */}
              <div className="hidden lg:block space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                    1
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">
                      {tEmbed('step1_title')}
                    </h3>
                    <p className="text-sm text-gray-300">
                      {tEmbed('step1_description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">
                      {tEmbed('step2_title')}
                    </h3>
                    <p className="text-sm text-gray-300">
                      {tEmbed('step2_description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">
                      {tEmbed('step3_title')}
                    </h3>
                    <p className="text-sm text-gray-300">
                      {tEmbed('step3_description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Embed Code - Right Side */}
            <div className="w-full lg:w-3/5 p-6 pt-2 lg:pt-6 border-t lg:border-t-0 lg:border-l border-white/10 min-w-0 flex-shrink">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-white mb-2">
                    {tEmbed('embed_code_title')}
                  </h3>
                  <div className="relative overflow-hidden">
                    <pre className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-4 text-sm overflow-x-auto font-mono max-w-full">
                      <code className="text-[#d4d4d4] whitespace-pre-wrap break-all">
                        <span className="text-[#569cd6]">&lt;div</span>{' '}
                        <span className="text-[#92c5f8]">style</span>=
                        <span className="text-[#ce9178]">
                          "padding: 10px 0; border-radius: 12px; overflow:
                          hidden;"
                        </span>
                        <span className="text-[#569cd6]">&gt;</span>
                        {'\n  '}
                        <span className="text-[#569cd6]">&lt;iframe</span>{' '}
                        {'\n    '}
                        <span className="text-[#92c5f8]">src</span>=
                        <span className="text-[#ce9178]">
                          "{embedProjectUrl}"
                        </span>
                        {'\n    '}
                        <span className="text-[#92c5f8]">style</span>=
                        <span className="text-[#ce9178]">
                          "width: 100%; aspect-ratio: 16 / 9; border: none;
                          border-radius: 12px;"
                        </span>
                        {'\n    '}
                        <span className="text-[#92c5f8]">allowfullscreen</span>
                        <span className="text-[#569cd6]">&gt;</span>
                        {'\n  '}
                        <span className="text-[#569cd6]">&lt;/iframe&gt;</span>
                        {'\n'}
                        <span className="text-[#569cd6]">&lt;/div&gt;</span>
                      </code>
                    </pre>
                    <Button
                      onClick={handleCopyEmbed}
                      disabled={!embedProjectUrl || !embedCode}
                      className={cn(
                        'absolute top-2 right-2 h-8 w-8 p-0 transition-colors',
                        copied
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-white/10 hover:bg-white/20',
                        (!embedProjectUrl || !embedCode) &&
                          'opacity-50 cursor-not-allowed',
                      )}
                      size="sm"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">
                    {tEmbed('preview_title')}
                  </h4>
                  <p className="text-sm text-gray-300 mb-2">
                    {tEmbed('preview_description')}
                  </p>
                  <div className="text-xs text-blue-400 font-mono break-all">
                    {embedProjectUrl}
                  </div>
                </div>

                <Button
                  onClick={handleCopyEmbed}
                  disabled={!embedProjectUrl || !embedCode}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  size="lg"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {tEmbed('copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      {tEmbed('copy_code')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    )
  }
  /** UI render */
  const domainLimitIsDefinedAndReached =
    domainLimits &&
    domainLimits.max_domains !== undefined &&
    domainLimits.remaining_domains !== undefined &&
    !domainLimits.can_create_more
  // Only determine needsUpgrade when data is loaded
  const needsUpgrade =
    !isSubscriptionDataLoading &&
    (domainLimitIsDefinedAndReached || !features.connectDomain)

  return (
    <DialogContent className="max-w-[700px] p-0 rounded-[20px] overflow-hidden border border-[#3B3B3B]">
      <div
        className="relative rounded-[20px] p-6 sm:p-8 overflow-hidden"
        style={{
          background:
            'linear-gradient(104deg, #222 1.87%, #2B2B2B 56.52%, #010101 111.18%)',
          boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.5)',
        }}
      >
        <DialogHeader className="mb-6">
          <DialogTitle className="text-xl sm:text-2xl font-semibold text-white text-left">
            {t('title')}
          </DialogTitle>
          <DialogDescription className="text-left text-gray-300 mt-2">
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Heyboss domain option - Selected by default */}
          <div className="rounded-xl p-5 bg-white/[0.06] border border-white/10">
            <div className="flex items-start gap-4">
              {/* Check icon in black circle */}
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black flex items-center justify-center mt-0.5">
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-white mb-1.5">
                  {t('free_option')}
                </div>
                <div className="text-sm text-gray-400">
                  {project?.status === 'live' ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {isPublishing ? (
                          <span className="text-white underline flex-1 min-w-0 break-words opacity-50 cursor-not-allowed">
                            {project?.deploy_url || projectUrl}
                          </span>
                        ) : (
                          <>
                            <a
                              href={project?.deploy_url || projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-white underline flex-1 min-w-0 break-words hover:text-white/80"
                            >
                              {project?.deploy_url || projectUrl}
                            </a>
                            <button
                              type="button"
                              onClick={async () => {
                                const url = project?.deploy_url || projectUrl
                                try {
                                  await navigator.clipboard.writeText(url)
                                  setCopiedDomain(true)
                                  toast.success('Copied to clipboard')
                                  setTimeout(() => {
                                    setCopiedDomain(false)
                                  }, 2000)
                                } catch (err) {
                                  console.error('Failed to copy:', err)
                                  toast.error('Failed to copy')
                                }
                              }}
                              className="flex-shrink-0 p-1.5 hover:bg-white/10 rounded text-white/80 hover:text-white transition-colors duration-200"
                              title="Copy URL"
                            >
                              {copiedDomain ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                      {isPublishing && (
                        <div className="flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />
                          <span className="text-xs text-gray-400">
                            {t('publishing_in_progress')}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>{t('publish_it_to_get_your_url')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Connect custom domain option */}
          <div className="rounded-xl p-5 bg-transparent border border-white/10">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="text-base font-semibold text-white">
                  {t('custom_option')}
                </div>
                <PremiumIcon width={18} height={18} />
              </div>
            </div>

            {/* Connect your own domain sub-option */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-white">
                  {t('own_domain_option')}
                </div>
                {project?.own_domain ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-300">
                      {project?.own_domain}
                    </span>
                    {getDomainStatus(project?.own_domain)}
                    <button
                      onClick={() => onUnbindDomain('own')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ) : isSubscriptionDataLoading ? (
                  <Button
                    className="bg-white/10 text-white rounded-lg px-4 py-2 h-auto flex items-center gap-2"
                    disabled
                    size="sm"
                  >
                    <Loader2 className="animate-spin w-4 h-4" />
                  </Button>
                ) : (
                  (() => {
                    return needsUpgrade ? (
                      <UpgradeButton
                        onClick={onUpgradeForConnectDomain}
                        isLoading={connectDomainLoading}
                      />
                    ) : (
                      <Button
                        onClick={onConnectDomain}
                        className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-4 py-2 h-auto flex items-center gap-2"
                        disabled={connectDomainLoading || needsUpgrade}
                        size="sm"
                      >
                        {connectDomainLoading && (
                          <Loader2 className="animate-spin w-4 h-4" />
                        )}
                        {t('connect_button')}
                        <PremiumIcon width={16} height={16} />
                      </Button>
                    )
                  })()
                )}
              </div>
              {/* Pending domain warning for own domain */}
              {project?.own_domain &&
                domainStatus &&
                !Boolean(domainStatus[project.own_domain]) && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-[#FDD577]/20 rounded-lg">
                    <Image
                      src="/ai-chat/triangle-warn-2.svg"
                      alt="Warning"
                      width={16}
                      height={16}
                      className="flex-shrink-0"
                    />
                    <div className="text-[#FDD577] text-xs break-all">
                      {t('domain_pending')}
                    </div>
                  </div>
                )}
            </div>

            {/* Get a new domain sub-option */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-white">
                  {t('new_domain_option')}
                </div>
                {project?.new_domain ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-300">
                      {project?.new_domain}
                    </span>
                    {getDomainStatus(project?.new_domain)}
                    <button
                      onClick={() => onUnbindDomain('new')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ) : isSubscriptionDataLoading ? (
                  <Button
                    className="bg-white/10 text-white rounded-lg px-4 py-2 h-auto flex items-center gap-2"
                    disabled
                    size="sm"
                  >
                    <Loader2 className="animate-spin w-4 h-4" />
                  </Button>
                ) : (
                  (() => {
                    return needsUpgrade ? (
                      <UpgradeButton
                        onClick={onUpgradeForBuyDomain}
                        isLoading={buyDomainLoading}
                      />
                    ) : (
                      <Button
                        onClick={onBuyDomain}
                        className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-4 py-2 h-auto flex items-center gap-2"
                        disabled={buyDomainLoading || needsUpgrade}
                        size="sm"
                      >
                        {buyDomainLoading && (
                          <Loader2 className="animate-spin w-4 h-4" />
                        )}
                        {t('connect_button')}
                        <PremiumIcon width={16} height={16} />
                      </Button>
                    )
                  })()
                )}
              </div>
              {/* Pending domain warning for new domain */}
              {project?.new_domain &&
                domainStatus &&
                !Boolean(domainStatus[project.new_domain]) && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-[#FDD577]/20 rounded-lg">
                    <Image
                      src="/ai-chat/triangle-warn-2.svg"
                      alt="Warning"
                      width={16}
                      height={16}
                      className="flex-shrink-0"
                    />
                    <div className="text-[#FDD577] text-xs">
                      {t('domain_pending')}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Embed in another web page option */}
          <div className="rounded-xl p-5 bg-transparent border border-white/10">
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-white">
                {t('embed_web_page')}
              </div>
              <GradientTooltip
                content={
                  !features.embeds
                    ? t('embed_premium_tooltip') ||
                      'Premium feature - Upgrade to use embed code'
                    : ''
                }
              >
                <Button
                  onClick={() => {
                    if (!features.embeds) {
                      pushModal('PricingModal', {
                        type: 'need_premium',
                        trigger: 'publish-modal-embed',
                        projectId: projectId,
                        minRequiredPlan: 'premium',
                      })
                      return
                    }
                    onEmbed()
                  }}
                  disabled={isEmbedding}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-4 py-2 h-auto flex items-center gap-2"
                  size="sm"
                >
                  {isEmbedding && (
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                  )}
                  {t('start_button')}
                  <CrownIcon className="w-4 h-4 text-[#FFBB00]" />
                </Button>
              </GradientTooltip>
            </div>
          </div>
        </div>

        {/* Publish button */}
        <div className="pt-6 mt-6 border-t border-white/10">
          <Button
            disabled={isPublishing}
            className="w-full bg-[#FDD577] hover:bg-[#FDD577]/90 text-black font-semibold rounded-lg py-3 px-6 h-auto"
            onClick={onPublish}
          >
            {isPublishing && (
              <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
            )}
            {t('publish_button')}
          </Button>
          {isPublishing && (
            <p className="text-xs text-gray-400 text-center mt-3">
              {t('publishing_status')}
            </p>
          )}
        </div>
      </div>

      {/* Disconnect Domain Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <DialogContent className="max-w-[450px] p-0 rounded-[20px] overflow-hidden border border-[#3B3B3B]">
          <div
            className="relative rounded-[20px] p-6 overflow-hidden"
            style={{
              background:
                'linear-gradient(104deg, #222 1.87%, #2B2B2B 56.52%, #010101 111.18%)',
            }}
          >
            <DialogHeader className="mb-4">
              <DialogTitle className="text-xl font-semibold text-white text-left">
                {t('disconnect_domain_title')}
              </DialogTitle>
              <DialogDescription className="text-left text-gray-300 mt-2">
                {t('disconnect_domain_description', {
                  domain:
                    (disconnectDomainType === 'own'
                      ? project?.own_domain
                      : project?.new_domain) || '',
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Multi-selector for reasons */}
              <div>
                <Label className="text-sm font-medium text-white mb-3 block">
                  {t('disconnect_reason_label')}
                </Label>
                <div className="space-y-2">
                  {disconnectReasonOptions.map((option) => {
                    return (
                      <div
                        key={option.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          return toggleDisconnectReason(option.id)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleDisconnectReason(option.id)
                          }
                        }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.06] hover:bg-white/10 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={disconnectReasons.includes(option.id)}
                          className="border-white/30 data-[state=checked]:bg-[#FDD577] data-[state=checked]:border-[#FDD577]"
                        />
                        <span className="text-sm text-white">
                          {option.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Text input for additional reason */}
              <div>
                <Label className="text-sm font-medium text-white mb-2 block">
                  {t('disconnect_other_reason_label')}
                </Label>
                <Textarea
                  value={disconnectOtherReason}
                  onChange={(e) => {
                    return setDisconnectOtherReason(e.target.value)
                  }}
                  placeholder={t('disconnect_other_reason_placeholder')}
                  className="bg-white/[0.06] border-white/10 text-white placeholder:text-gray-500 min-h-[80px] resize-none"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                disabled={isDisconnecting}
                onClick={() => {
                  return setShowDisconnectDialog(false)
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                {t('disconnect_cancel')}
              </Button>
              <Button
                onClick={onConfirmUnbindDomain}
                disabled={isDisconnecting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isDisconnecting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {t('disconnect_confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DialogContent>
  )
}
